import { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import StatusBadge from '@shared/components/atoms/StatusBadge';

import styles from './MobileApprovalList.module.scss';

/**
 * yyyyMMddHHmmss → "YYYY.MM.DD" 형태로 변환
 * @param {string} rawDate - 예: "20251202154926"
 * @returns {string} 변환된 날짜 문자열 (예: "2025.12.02")
 */
function formatDateShort(rawDate) {
  if (!rawDate || rawDate.length < 8) {
    return '';
  }

  const year = rawDate.slice(0, 4);
  const month = rawDate.slice(4, 6);
  const day = rawDate.slice(6, 8);

  return `${year}.${month}.${day}`;
}

/**
 * 모바일 결재 리스트 컴포넌트
 * - Figma 디자인 기반 리스트 아이템
 * - 무한 스크롤 지원
 */
function MobileApprovalList({
  items,
  onItemClick,
  onLoadMore,
  hasMore,
  isLoading,
  isFetching,
  emptyMessage,
  renderText, // 데이터 형태가 다를 경우를 위한 props

  selectable, // 체크박스 표시 여부 (boolean)
  selectedIds, // 선택된 아이템 id 배열
  onSelectChange, // 선택 변경 콜백
  getItemId, // item → 고유 id 추출 함수
}) {
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // 무한 스크롤을 위한 Intersection Observer
  const handleObserver = useCallback(
    function (entries) {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore],
  );

  useEffect(
    function () {
      const options = {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      };

      observerRef.current = new IntersectionObserver(handleObserver, options);

      if (loadMoreRef.current) {
        observerRef.current.observe(loadMoreRef.current);
      }

      return function () {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    },
    [handleObserver],
  );

  if (items.length === 0 && !isLoading) {
    return (
      <div className={styles.emptyContainer}>
        <p className={styles.emptyMessage}>{emptyMessage}</p>
      </div>
    );
  }
  //  체크박스 선택 토글 함수
  function handleToggleSelect(item, event) {
    event.stopPropagation();

    if (!selectable) {
      return;
    }

    const itemId = selectable ? getItemId(item) : null;
    if (itemId == null) {
      return;
    }

    if (selectedIds.includes(itemId)) {
      onSelectChange(selectedIds.filter((v) => v !== itemId));
    } else {
      onSelectChange([...selectedIds, itemId]);
    }
  }

  //기본 렌더링 형식
  function defaultRenderText(item, isSelected, isCompleted) {
    return (
      <div className={styles.textFrame}>
        {/* 제목 */}
        <span
          className={`${styles.title} ${
            isSelected ? styles.selectedTitle : ''
          } ${isCompleted ? styles.completedText : ''}`}
        >
          {item.title || item.docType}
        </span>
        {/* 문서유형 */}
        <span
          className={`${styles.subtitle} ${isCompleted ? styles.completedText : ''} ${styles.subType}`}
        >
          {item.docType}
        </span>
        {/* 부서와 이름 */}
        <span className={`${styles.subtitle} ${isCompleted ? styles.completedText : ''}`}>
          {formatDateShort(item.date)} {item.team}-{item.name}
        </span>
      </div>
    );
  }

  // 1. 최초 로딩
  if (isLoading || (isFetching && items.length === 0)) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.circularProgress} />
        <div className={styles.loadingMessage}>문서 목록을 불러오는 중입니다...</div>
      </div>
    );
  }

  // 2. 빈 상태
  if (!items || items.length === 0) {
    return <div className={styles.emptyMessage}>{emptyMessage}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.list}>
        {items.map(function (item, index) {
          const isLast = index === items.length - 1;
          const itemId = selectable ? getItemId(item) : null;
          const isSelected = selectable && itemId != null && selectedIds.includes(itemId);
          // 완료 상태
          const isCompleted = item.exeState === 1 || item.exeState === '1';

          return (
            <div key={item.idx || index}>
              <button type="button" className={styles.listRow} onClick={() => onItemClick(item)}>
                {/* 체크박스 */}
                {selectable && (
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={isSelected}
                    onChange={(e) => handleToggleSelect(item, e)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}

                {/* 텍스트 */}
                {renderText ? renderText(item) : defaultRenderText(item, isSelected, isCompleted)}

                {/* 상태 배지 */}
                <StatusBadge state={item.state} isCompleted={isCompleted} />
              </button>

              {!isLast && <div className={styles.divider} />}
            </div>
          );
        })}
      </div>

      {/* 로딩 인디케이터 */}
      {/* {isLoading && (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <span className={styles.loadingText}>불러오는 중...</span>
        </div>
      )} */}

      {/* 무한 스크롤 트리거 요소 */}
      {hasMore && !isLoading && <div ref={loadMoreRef} className={styles.loadMoreTrigger} />}
    </div>
  );
}

MobileApprovalList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      docType: PropTypes.string,
      docNumber: PropTypes.string,
      title: PropTypes.string,
      date: PropTypes.string,
      team: PropTypes.string,
      name: PropTypes.string,
      state: PropTypes.string,
      renderText: PropTypes.func,
    }),
  ),

  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onSelectChange: PropTypes.func,
  getItemId: PropTypes.func,
  onItemClick: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
};

MobileApprovalList.defaultProps = {
  items: [],
  onItemClick: () => {},

  selectable: false,
  selectedIds: [],
  onSelectChange: () => {},
  getItemId: (item) => item.idx,

  onLoadMore: () => {},
  hasMore: false,
  isLoading: false,
  emptyMessage: '문서가 없습니다.',
  renderText: null,
};

export default MobileApprovalList;
